import './landing.css';
import { Accordion, Button, Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ArrowRightIcon, CaretRightIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Logo, MenuButton } from '../../../components/common';
import { FAQ, FEATURES, NAV, PATTERNS, STEPS, STUDENT_POINTS, TEACHER_POINTS, TRUST } from './content';
import { DemoForm } from './DemoForm';
import { HeroVisual, StudentsVisual, TeachersVisual } from './Mockups';
import { useSitePages } from './sitePages';

/*
  The public website. Rendered by the app at "/" and also pre-rendered to static HTML at build
  time (src/prerender.tsx), so it must render the same on the server: no browser APIs during
  render, only in effects.
*/

function useScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/**
 * `simple`: for pages without JavaScript (the legal pages): no menu, every link a plain link
 * back to the website.
 */
function SiteHeader({ simple = false }: { simple?: boolean }) {
  const scrolled = useScrolled();
  const [opened, { toggle, close }] = useDisclosure(false);
  const demo = simple ? '/#demo' : '#demo';
  return (
    <header className="lp-header" data-scrolled={scrolled}>
      <div className="lp-container lp-header__bar">
        <a href={simple ? '/' : '#top'} className="lp-header__logo" aria-label="Testpio home">
          <Logo size={34} />
        </a>
        {!simple && (
          <nav className="lp-nav" aria-label="Main">
            {NAV.map((n) => (
              <a key={n.href} href={n.href}>
                {n.label}
              </a>
            ))}
          </nav>
        )}
        <div className="lp-header__actions" data-simple={simple}>
          <Button component={Link} to="/login" variant="subtle" color="gray" size="md">
            Sign in
          </Button>
          <Button component="a" href={demo} size="md">
            Book a demo
          </Button>
        </div>
        {!simple && <MenuButton hiddenFrom="md" opened={opened} onClick={toggle} />}
      </div>
      {!simple && (
        <Drawer opened={opened} onClose={close} position="right" size="min(100%, 360px)" title={<Logo size={30} />} padding="lg">
          <nav className="lp-drawer-links" aria-label="Main">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={close}>
                {n.label}
                <CaretRightIcon size={18} color="var(--mantine-color-gray-5)" />
              </a>
            ))}
          </nav>
          <Button component="a" href="#demo" onClick={close} size="lg" fullWidth mt="xl">
            Book a demo
          </Button>
          <Button component={Link} to="/login" variant="default" size="lg" fullWidth mt="sm">
            Sign in
          </Button>
        </Drawer>
      )}
    </header>
  );
}

function SiteFooter({ onWebsite = true }: { onWebsite?: boolean }) {
  const base = onWebsite ? '' : '/';
  const pages = useSitePages();
  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer__grid">
          <div className="lp-footer__brand">
            <Logo size={32} />
            <p className="lp-body" style={{ marginTop: 16, maxWidth: 360 }}>
              Online tests, ranks and progress reports for coaching institutes. Made in India, in Hindi and English.
            </p>
          </div>
          <div>
            <div className="lp-footer__title">Product</div>
            <ul className="lp-footer__links">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={base + n.href}>{n.label}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="lp-footer__title">Company</div>
            {/* The Worker adds the published pages to #footer-pages in the HTML (worker/html.ts). */}
            <ul className="lp-footer__links" id="footer-pages">
              <li>
                <a href={`${base}#demo`}>Book a demo</a>
              </li>
              <li>
                <Link to="/login">Sign in to your institute</Link>
              </li>
              {pages.map((p) => (
                <li key={p.slug}>
                  <a href={`/${p.slug}`}>{p.title}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="lp-footer__bottom">
          <span>© {new Date().getFullYear()} Testpio</span>
        </div>
      </div>
    </footer>
  );
}

function Check({ children }: { children: ReactNode }) {
  return (
    <li>
      <span className="lp-list__icon">
        <CheckCircleIcon size={20} weight="fill" />
      </span>
      <span>{children}</span>
    </li>
  );
}

function SectionHead({ eyebrow, title, lead }: { eyebrow: string; title: ReactNode; lead?: ReactNode }) {
  return (
    <div className="lp-section-head">
      <span className="lp-eyebrow">{eyebrow}</span>
      <h2 className="lp-h2">{title}</h2>
      {lead && <p className="lp-lead">{lead}</p>}
    </div>
  );
}

export function LandingPage() {
  return (
    <div className="lp" id="top">
      <SiteHeader />

      <main>
        {/* ------------------------------------------------------------ hero */}
        <section className="lp-hero">
          <div className="lp-hero__bg" aria-hidden="true" />
          <div className="lp-container lp-hero__grid">
            <div className="lp-hero__copy">
              <span className="lp-chip">
                <span className="lp-chip__dot" />
                Online test series for coaching institutes
              </span>
              <h1 className="lp-h1">
                The real exam experience, <span className="lp-gradient-text">before the real exam.</span>
              </h1>
              <p className="lp-lead">
                Run mock tests on your students’ own phones, in Hindi and English. A proper exam screen, fair ranks and percentiles, and progress reports that students and parents understand.
              </p>
              <div className="lp-hero__ctas">
                <Button component="a" href="#demo" size="lg" rightSection={<ArrowRightIcon size={18} weight="bold" />}>
                  Book a demo
                </Button>
                <Button component={Link} to="/login" size="lg" variant="default">
                  Sign in to your institute
                </Button>
              </div>
              <ul className="lp-checks">
                <li>
                  <CheckCircleIcon size={18} weight="fill" />
                  No app to install
                </li>
                <li>
                  <CheckCircleIcon size={18} weight="fill" />
                  Works on low-cost phones
                </li>
                <li>
                  <CheckCircleIcon size={18} weight="fill" />
                  हिंदी + English
                </li>
              </ul>
            </div>
            <div className="lp-hero__visual">
              <HeroVisual />
            </div>
          </div>
        </section>

        {/* -------------------------------------------------------- patterns */}
        <section className="lp-patterns" aria-labelledby="patterns-title">
          <div className="lp-container">
            <p className="lp-patterns__label" id="patterns-title">
              Ready-made exam patterns, with the right marks and negative marking
            </p>
            <ul className="lp-patterns__list">
              {PATTERNS.map((p) => (
                <li key={p}>{p}</li>
              ))}
              <li data-accent="true">+ Your own pattern</li>
            </ul>
          </div>
        </section>

        {/* -------------------------------------------------------- features */}
        <section className="lp-section" id="features">
          <div className="lp-container">
            <SectionHead
              eyebrow="Features"
              title="Everything you need to run online tests"
              lead="Built for how coaching institutes in India work: WhatsApp groups, batches that read Hindi and English, and students on budget phones."
            />
            <div className="lp-features">
              {FEATURES.map((f) => (
                <article key={f.title} className="lp-card lp-feature">
                  <span className="lp-icon">
                    <f.icon size={24} weight="duotone" />
                  </span>
                  <h3 className="lp-h3">{f.title}</h3>
                  <p className="lp-body">{f.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------- how it works */}
        <section className="lp-section lp-section--soft" id="how-it-works">
          <div className="lp-container">
            <SectionHead eyebrow="How it works" title="From questions to ranks in three steps" />
            <ol className="lp-steps" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {STEPS.map((s, i) => (
                <li key={s.title} className="lp-card lp-step">
                  <span className="lp-step__num">{i + 1}</span>
                  <h3 className="lp-h3">{s.title}</h3>
                  <p className="lp-body">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* -------------------------------------------------------- students */}
        <section className="lp-section" id="students">
          <div className="lp-container lp-split">
            <div>
              <span className="lp-eyebrow">For students and parents</span>
              <h2 className="lp-h2">Results students are proud to share</h2>
              <p className="lp-lead">After every test, students see where they stand and what to work on next. Parents see real progress, not just marks.</p>
              <ul className="lp-list">
                {STUDENT_POINTS.map(([strong, rest]) => (
                  <Check key={strong}>
                    <b>{strong}</b>
                    {rest}
                  </Check>
                ))}
              </ul>
            </div>
            <StudentsVisual />
          </div>
        </section>

        {/* -------------------------------------------------------- teachers */}
        <section className="lp-section lp-section--soft" id="teachers">
          <div className="lp-container lp-split lp-split--reverse">
            <div>
              <span className="lp-eyebrow">For teachers and owners</span>
              <h2 className="lp-h2">Every batch at a glance</h2>
              <p className="lp-lead">Know who is writing, who is slipping and which questions need a second look, without opening a spreadsheet.</p>
              <ul className="lp-list">
                {TEACHER_POINTS.map(([strong, rest]) => (
                  <Check key={strong}>
                    <b>{strong}</b>
                    {rest}
                  </Check>
                ))}
              </ul>
            </div>
            <TeachersVisual />
          </div>
        </section>

        {/* -------------------------------------------------------- security */}
        <section className="lp-section" id="security">
          <div className="lp-container">
            <SectionHead eyebrow="Fair and secure" title="Results everyone can trust" lead="Ranks only mean something when the test is fair. Testpio is built so that it is." />
            <div className="lp-trust">
              {TRUST.map((item) => (
                <article key={item.title} className="lp-card lp-trust__item">
                  <span className="lp-icon">
                    <item.icon size={24} weight="duotone" />
                  </span>
                  <h3 className="lp-h3">{item.title}</h3>
                  <p className="lp-body">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------- faq */}
        <section className="lp-section lp-section--soft" id="faq">
          <div className="lp-container">
            <SectionHead eyebrow="FAQ" title="Questions institutes ask" />
            <Accordion className="lp-faq" variant="unstyled" chevronPosition="right" defaultValue={FAQ[0]?.q}>
              {FAQ.map((f) => (
                <Accordion.Item key={f.q} value={f.q}>
                  <Accordion.Control>{f.q}</Accordion.Control>
                  <Accordion.Panel>{f.a}</Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ------------------------------------------------------------ demo */}
        <section className="lp-demo" id="demo">
          <div className="lp-container lp-demo__grid">
            <div>
              <span className="lp-eyebrow">Book a demo</span>
              <h2 className="lp-h2">See Testpio with your own questions</h2>
              <p className="lp-lead">Tell us a little about your institute. We will walk you through a complete test, from adding questions to releasing ranks.</p>
              <ul className="lp-list">
                <Check>See the exam screen on a phone, in Hindi and English</Check>
                <Check>Set up a test from your own questions and patterns</Check>
                <Check>Ask anything about ranks, results and reports</Check>
              </ul>
            </div>
            <div className="lp-form-card">
              <DemoForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

/**
 * The frame of website pages (/privacy, /terms and pages added in the platform console).
 * Pre-rendered once at build time; the Worker fills in #page-title, #page-updated and
 * #page-body with the published text.
 */
export function PageLayout() {
  return (
    <div className="lp">
      <SiteHeader simple />
      <main className="lp-legal">
        <div className="lp-container lp-legal__inner">
          <h1 id="page-title" className="lp-h2 lp-legal__title">
            Testpio
          </h1>
          <p id="page-updated" className="lp-legal__updated" />
          <article id="page-body" className="legal-content lp-legal__body" />
        </div>
      </main>
      <SiteFooter onWebsite={false} />
    </div>
  );
}
